import { axiosRubyClient } from '@/util/axios/axiosClient';

// Preferred-worker pool management (GRAVY-10265). Tenant-admin only (P10);
// every endpoint 404s while PREFERRED_WORKER_POOLS_ENABLED is unset (P13),
// which listPools translates into { enabled: false } so the UI renders nothing.

export type PoolHealth = 'red' | 'amber' | 'green';
export type PoolTier = 'monitored' | 'standard' | 'gold' | 'platinum';
// GRAVY-10943: favorites that could not go straight into a pool — the
// waiting list (pool at its cap) and multi-skill favorites ops must place.
export type NominationSource =
  | 'client_nomination'
  | 'system_suggestion'
  | 'favorite_over_cap'
  | 'favorite_skill_unconfirmed';

export interface PoolSkill {
  id: number;
  name: string | null;
}

export interface PoolRow {
  id: number;
  skill: PoolSkill;
  active_member_count: number;
  target_depth: number;
  alert_threshold: number;
  health: PoolHealth;
  pending_nominations_count: number;
  rookie_slots: number;
  rookie_slots_filled: number;
  // GRAVY-10943: the hard cap on active members (default 20). Optional so a
  // frontend deployed ahead of graphy still type-checks.
  max_members?: number;
  status: 'active' | 'archived' | 'inactive';
}

export interface PoolDetail extends PoolRow {
  customer: { id: number; name: string | null };
}

export interface PoolWorker {
  id: number;
  name: string | null;
  avatar_url: string | null;
}

// GRAVY-10363: approved shifts per skill (all customers), most-worked first.
export interface PoolWorkerSkill {
  id: number;
  name: string | null;
  image_url: string | null;
  shifts_count: number;
}

interface PoolWorkerBlock {
  worker: PoolWorker;
  tier: PoolTier | null;
  tier_source: 'dependability_v2' | 'none';
  rookie: boolean;
  shifts_in_skill_total: number;
  shifts_with_client: number;
  // GRAVY-10363 Review-modal summary inputs. Optional so a backend deployed
  // behind this UI still type-checks; the modal renders "—" when absent.
  tax_type?: '1099' | 'w2' | null;
  preferred_pools_count?: number;
  skills?: PoolWorkerSkill[];
}

// GRAVY-10394: the one-time backfill review returned by createPool — workers
// with a qualifying 5★ for this skill from before the pool existed. Never
// persisted server-side; ignoring the review is final by construction.
export interface BackfillCandidate extends PoolWorkerBlock {
  qualifying_rating_count: number;
  last_rated_at: string | null;
}

export interface BackfillReview {
  window: 'full_history' | '90_days';
  candidates: BackfillCandidate[];
}

export interface PoolMemberRow extends PoolWorkerBlock {
  membership_id: number;
  worker_active: boolean;
  // GRAVY-10395: staffing readiness — optional so a frontend deployed ahead
  // of graphy tolerates the fields' absence.
  worker_ready_to_work?: boolean;
  not_ready_reason?: string | null;
  source: string;
  added_at: string | null;
}

export interface ClientNominationReview {
  timesheet_id: number | null;
  job_id: number | null;
  shift_start_at: string | null;
  account_name: string | null;
  rating: number | null;
  rated_at: string | null;
  rated_by: 'client' | 'internal' | null;
}

// GRAVY-10393: set by the suggestion scan when the worker's score drops
// below Gold while the suggestion is pending; cleared on re-qualification.
export interface StaleInfo {
  at: string | null;
  score: number | null;
  tier: string | null;
}

export interface SystemNominationReview {
  qualifying_shift_count: number | null;
  qualifying_score: number | null;
  skill_name: string | null;
  market_name: string | null;
  // GRAVY-10393: live values alongside the frozen snapshot — optional so a
  // frontend deployed ahead of graphy tolerates their absence.
  current_score?: number | null;
  current_tier?: string | null;
  current_shift_count?: number | null;
  stale?: StaleInfo | null;
  // GRAVY-10943: on favorite nominations only — the place on the waiting
  // list, or the skills ops must choose between.
  favorite?: FavoriteNominationInfo | null;
}

export interface FavoriteNominationInfo {
  rank: number | null;
  candidate_skills: string[];
}

export interface PoolNominationRow extends PoolWorkerBlock {
  nomination_id: number;
  source: NominationSource;
  occurred_at: string | null;
  review: ClientNominationReview | SystemNominationReview;
  stale?: StaleInfo | null;
  also_qualified: Record<string, unknown> | null;
}

export interface AlsoApproved {
  skill_id: number;
  approved: boolean;
  error: string | null;
}

export interface PoolPagination {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

// GRAVY-10943 FP-4.5: the client's "Pools ready" summary. Absent on a graphy
// without the from-favorites build.
export interface PoolSeeding {
  follows_custom_rates: boolean;
  pools_ready: boolean;
  backfilled_at: string | null;
  // Review D2-01: false for a client only the five-minute sweep seeded (no
  // backfill run, so no report). Absent on a graphy without it: show.
  report_available?: boolean;
  seeded_count: number;
  waiting_count: number;
  needs_skill_confirmed_count: number;
}

export interface PoolsListResult {
  enabled: boolean;
  pools: PoolRow[];
  seeding?: PoolSeeding;
}

export interface PageParams {
  page?: number;
  per_page?: number;
}

export interface CreatePoolParams {
  skill_id: number;
  target_depth?: number;
  alert_threshold?: number;
}

export interface UpdatePoolParams {
  target_depth?: number;
  alert_threshold?: number;
  status?: 'archived';
  max_members?: number;
}

interface ApiErrorShape {
  isAxiosError?: boolean;
  response?: { status?: number; data?: { errors?: string[] } };
}

const blobError = async (error: unknown): Promise<unknown> => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (!(data instanceof Blob)) return error;
  try {
    const parsed = JSON.parse(await data.text());
    if (Array.isArray(parsed?.errors) && parsed.errors.length > 0) {
      return new Error(parsed.errors.join(', '));
    }
  } catch {
    // Not JSON: fall back to the generic message.
  }
  return error;
};

const gatedStatus = (error: unknown): boolean => {
  const status = (error as ApiErrorShape)?.response?.status;
  return status === 404 || status === 403;
};

export const extractPoolsError = (error: unknown, fallback: string): string => {
  const errors = (error as ApiErrorShape)?.response?.data?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    return errors.join(', ');
  }
  return fallback;
};

export class PreferredPoolsAPI {
  static async listPools(
    customerId: number | string,
  ): Promise<PoolsListResult> {
    try {
      const response = await axiosRubyClient.get(
        `/api/admin/customers/${customerId}/preferred_pools`,
      );
      return response.data.data as PoolsListResult;
    } catch (error) {
      if (gatedStatus(error)) return { enabled: false, pools: [] };
      throw error;
    }
  }

  static async createPool(
    customerId: number | string,
    params: CreatePoolParams,
  ): Promise<PoolRow & { backfill_review?: BackfillReview }> {
    const response = await axiosRubyClient.post(
      `/api/admin/customers/${customerId}/preferred_pools`,
      params,
    );
    return response.data.data as PoolRow & { backfill_review?: BackfillReview };
  }

  static async getPool(poolId: number): Promise<PoolDetail> {
    const response = await axiosRubyClient.get(
      `/api/admin/preferred_pools/${poolId}`,
    );
    return response.data.data as PoolDetail;
  }

  static async updatePool(
    poolId: number,
    params: UpdatePoolParams,
  ): Promise<PoolDetail> {
    const response = await axiosRubyClient.patch(
      `/api/admin/preferred_pools/${poolId}`,
      params,
    );
    return response.data.data as PoolDetail;
  }

  static async listMembers(
    poolId: number,
    params: PageParams = {},
  ): Promise<{ members: PoolMemberRow[]; pagination: PoolPagination }> {
    const response = await axiosRubyClient.get(
      `/api/admin/preferred_pools/${poolId}/members`,
      { params },
    );
    return {
      members: response.data.data as PoolMemberRow[],
      pagination: response.data.pagination as PoolPagination,
    };
  }

  static async addMember(
    poolId: number,
    params: { worker_id: number; reason?: string; source?: 'backfill_review' },
  ): Promise<PoolMemberRow> {
    const response = await axiosRubyClient.post(
      `/api/admin/preferred_pools/${poolId}/members`,
      params,
    );
    return response.data.data as PoolMemberRow;
  }

  static async removeMember(
    poolId: number,
    membershipId: number,
    reason?: string,
  ): Promise<void> {
    await axiosRubyClient.delete(
      `/api/admin/preferred_pools/${poolId}/members/${membershipId}`,
      { data: { reason } },
    );
  }

  static async listNominations(
    poolId: number,
    params: PageParams = {},
  ): Promise<{ nominations: PoolNominationRow[]; pagination: PoolPagination }> {
    const response = await axiosRubyClient.get(
      `/api/admin/preferred_pools/${poolId}/nominations`,
      { params },
    );
    return {
      nominations: response.data.data as PoolNominationRow[],
      pagination: response.data.pagination as PoolPagination,
    };
  }

  // GRAVY-10943 Q9: `allSkills` on a "needs skill confirmed" favorite also
  // approves the worker's other candidate pools; `also_approved` says how
  // each went (a full pool refuses on its own, the rest still go in).
  static async approveNomination(
    poolId: number,
    nominationId: number,
    note?: string,
    allSkills = false,
  ): Promise<PoolMemberRow & { also_approved?: AlsoApproved[] }> {
    const response = await axiosRubyClient.post(
      `/api/admin/preferred_pools/${poolId}/nominations/${nominationId}/approve`,
      { ...(note ? { note } : {}), ...(allSkills ? { all_skills: true } : {}) },
    );
    return response.data.data as PoolMemberRow & {
      also_approved?: AlsoApproved[];
    };
  }

  // GRAVY-10943 FP-4.4: the latest backfill run's report for one client, as
  // a CSV file. A failure arrives as a Blob body (responseType), so its
  // `errors` are read out of it and thrown as a plain Error message.
  // `detail: 'workers'` is one row per worker, by name (Product, 2026-10-21).
  static async downloadBackfillReport(
    customerId: number | string,
    detail?: 'workers',
  ): Promise<{
    blob: Blob;
    filename: string;
  }> {
    let response;
    try {
      response = await axiosRubyClient.get(
        '/api/admin/preferred_pools/backfill_report.csv',
        {
          params: { customer_id: customerId, ...(detail ? { detail } : {}) },
          responseType: 'blob',
        },
      );
    } catch (error) {
      throw await blobError(error);
    }
    const disposition = String(response.headers?.['content-disposition'] ?? '');
    const match = disposition.match(/filename="?([^";]+)"?/);
    return {
      blob: response.data as Blob,
      filename: match?.[1] ?? 'preferred-pools-backfill.csv',
    };
  }

  static async declineNomination(
    poolId: number,
    nominationId: number,
    reason?: string,
  ): Promise<void> {
    await axiosRubyClient.post(
      `/api/admin/preferred_pools/${poolId}/nominations/${nominationId}/decline`,
      reason ? { reason } : {},
    );
  }
}

export const isClientReview = (
  review: ClientNominationReview | SystemNominationReview,
  source: NominationSource,
): review is ClientNominationReview => source === 'client_nomination';
